import { css, styled } from '@sds-system/system';
import { getAllStyledProps } from '@sds-system/utils';
import type {
  StyledDatePickerDayProps,
  StyledDatePickerHeaderProps,
  StyledDatePickerRootProps,
  StyledDatePickerWrapperProps,
} from './DatePicker.types';

export const StyledDatePickerRoot = styled('div')<StyledDatePickerRootProps>(
  ({ theme, styledDatePickerDay }) => {
    return css`
      width: 304px;
      height: max-content;

      .react-datepicker__tab-loop {
        position: relative;
        margin-top: 0px;
        z-index: 1;
      }
      /* 전체 팝업 */
      .react-datepicker-popper {
        top: 8px;
        width: 100%;
        max-width: 304px;
        padding: 12px;
        border-radius: ${theme.borderRadius[8]}px;
        box-shadow: ${theme.shadow.center.normal};
        transform: none !important;
        background-color: ${theme.palette.system.white};
      }

      .react-datepicker__aria-live {
        display: none;
      }

      /* 요일 */
      .react-datepicker__day-names {
        width: 100%;
        display: flex;
        margin-top: 4px;
        background-color: ${theme.palette.bg.tertiary};

        font-size: ${theme.typography.body2.regular.fontSize};
        font-weight: ${theme.typography.body2.regular.fontWeight};
        letter-spacing: ${theme.typography.body2.regular.letterSpacing};
        line-height: ${theme.typography.body2.regular.lineHeight};
        color: ${theme.palette.text.secondary};

        & > div {
          width: 100%;
          height: 40px;
          display: flex;
          justify-content: center;
          align-items: center;
        }
      }

      /* 주별 */
      .react-datepicker__week {
        width: 100%;
        display: flex;

        & > div {
          width: 100%;
        }
      }

      /* 일별 */
      .react-datepicker__day {
        &:focus {
          outline: none;
        }

        width: 100%;
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        color: ${theme.palette.text.secondary};
        border-radius: 8px;

        font-size: ${theme.typography.body2.regular.fontSize};
        font-weight: ${theme.typography.body2.regular.fontWeight};
        letter-spacing: ${theme.typography.body2.regular.letterSpacing};
        line-height: ${theme.typography.body2.regular.lineHeight};

        &:hover {
          color: ${theme.palette.text.secondary};
          background-color: ${theme.palette.bg.interactive.selectedHover};
        }

        &.react-datepicker__day--today {
          background-color: ${theme.palette.system.white};
          color: ${theme.palette.text.interactive.primary};
        }

        &.react-datepicker__day--outside-month {
          color: ${theme.palette.text.tertiary};
        }

        &.react-datepicker__day--selected {
          background-color: ${theme.palette.bg.interactive.primary};
          color: ${theme.palette.text.primaryInverse};
        }

        &.react-datepicker__day--in-range,
        &.react-datepicker__day--in-selecting-range {
          border-radius: 0px;
          background-color: ${theme.palette.bg.interactive.selected};
        }

        &.react-datepicker__day--range-start,
        &.react-datepicker__day--range-end,
        &.react-datepicker__day--selecting-range-start,
        &.react-datepicker__day--selecting-range-end {
          background-color: ${theme.palette.bg.interactive.primary};
          color: ${theme.palette.system.white};
        }

        &.react-datepicker__day--range-start,
        &.react-datepicker__day--selecting-range-start {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
        }
        &.react-datepicker__day--range-end,
        &.react-datepicker__day--selecting-range-end {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
        }
        &.react-datepicker__day--range-start.react-datepicker__day--range-end {
          border-radius: 8px;
        }

        ${getAllStyledProps?.map((getStyled) => getStyled({ ...styledDatePickerDay, theme }))}
      }
    `;
  },
  getAllStyledProps,
);

export const StyledDatePickerHeader = styled('div')<StyledDatePickerHeaderProps>`
  display: flex;
  width: 100%;
  padding: 4px;
  justify-content: space-between;
  align-items: center;
  ${getAllStyledProps}
`;

export const StyledDatePickerDay = styled('div')<StyledDatePickerDayProps>(() => {
  return {};
}, getAllStyledProps);

export const StyledDatePickerWrapper = styled('div')<StyledDatePickerWrapperProps>(
  ({ theme, size, disabled, isActive, isFilled, isError, errorMessages }) => {
    return {
      width: '100%',
      height: theme.size[size],
      padding: '8px 16px',
      color: theme.palette.text.primary,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: `1px solid ${theme.palette.border.secondary}`,
      borderRadius: theme.borderRadius[8],
      backgroundColor: theme.palette.bg.primary,
      cursor: 'pointer',

      input: {
        ...(size === 'XL' ? theme.typography.body.regular : theme.typography.body2.regular),
        width: '100%',
        border: 'none',
        margin: 0,
        padding: 0,
        color: 'inherit',

        '&::placeholder': {
          color: theme.palette.text.tertiary,
        },
        '&:focus': { outline: 'none' },
      },

      ...(isFilled && {
        color: theme.palette.text.interactive.primary,
      }),
      ...(isActive && {
        border: `1px solid ${theme.palette.border.interactive.primary}`,
        color: theme.palette.text.primary,
      }),
      ...(disabled && {
        cursor: 'not-allowed',
        border: `1px solid ${theme.palette.border.disabled}`,
        color: theme.palette.text.disabled,
      }),
      ...((isError || errorMessages.length > 0) && {
        border: `1px solid ${theme.palette.border.error}`,
        color: theme.palette.text.error,
      }),
    };
  },
  getAllStyledProps,
);

export const StyledDatePickerEnterWrapper = styled(StyledDatePickerWrapper)(({ isRangeMode }) => {
  return {
    cursor: 'auto',
    gap: 8,

    input: {
      textAlign: isRangeMode ? 'center' : 'start',
    },
  };
});
